import starIcon from "@loot-os/icons/icons/star";
import { Avatar } from "../core/Avatar.js";
import { cardStyles } from "../core/Card.styles.js";
import { Icon } from "../core/Icon.js";
import { cn } from "../foundations/cn.js";
import { ItemArt } from "./ItemArt.js";

export interface ReviewCardProps {
  author: string;
  avatar?: string;
  /** When it was written, already formatted ("Sep 2026"). */
  date: string;
  /** 1 to 5. */
  rating: number;
  quote: string;
  /** The item they pulled, if the review is about one. */
  pull?: { name: string; image: string };
  /** How the rating is read out; tenant copy. */
  ratingLabel?: (rating: number) => string;
  className?: string;
}

/** One shopper's review: who, when, a star rating, the quote and what they pulled. */
export function ReviewCard({
  author,
  avatar,
  date,
  rating,
  quote,
  pull,
  ratingLabel = (r) => `${r} out of 5 stars`,
  className,
}: ReviewCardProps) {
  return (
    <figure className={cn(cardStyles({ radius: "2xl" }), "flex flex-col gap-4 p-5", className)}>
      <figcaption className="flex items-center gap-3">
        <Avatar name={author} src={avatar} size="md" decorative />
        <span className="flex flex-col">
          <span className="font-body text-body-b1-strong text-text-primary">{author}</span>
          <span className="font-body text-body-b3 text-text-tertiary">{date}</span>
        </span>
      </figcaption>
      <span role="img" aria-label={ratingLabel(rating)} className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <Icon
            key={n}
            icon={starIcon}
            size={14}
            className={n <= rating ? "fill-rarity-chase text-rarity-chase" : "text-text-disabled"}
          />
        ))}
      </span>
      <blockquote className="font-body text-body-b1 text-text-secondary">{quote}</blockquote>
      {pull && (
        <span className="flex items-center gap-3 border-t border-border-subtle pt-4">
          <ItemArt src={pull.image} alt="" className="h-12 w-9 shrink-0" />
          <span className="font-body text-body-b3-strong text-text-primary">{pull.name}</span>
        </span>
      )}
    </figure>
  );
}
